<template>
  <h1 class="page-title">Les catégories associées au produit</h1>
  <div>
    <tree
      :nodes="nodes"
      :config="TreeConfig"
      @nodeChecked="onCategoryNodeChecked($event)"
      @nodeUnchecked="onCategoryNodeUnchecked($event)"
    />
  </div>

  <div class="hidden">
    <FormKit v-for="(catItem, i) in selectedCategoriesUuids"  :key="i"
             type="checkbox"
             :name="[catItem]"
             value="true"
    />
  </div>

  <FormKit
    v-model="selectedCategoriesUuids"
    type="list"
    name="hasSelectedCategory"
    validation="required"
    :validation-messages="{
            required: 'Votre produit doit être associé à une catégorie minimum.',
        }"
    input-class="$reset hidden"
    outer-class="$reset"
    inner-class="$reset"
  />
</template>

<script setup lang="ts">
// IMPORTS
import { ref, onMounted } from "vue";
import Tree from "vue3-treeview";
import {useRoute} from "vue-router";
import swal from "../../../../_helpers/swal";
import ApiService from "@/_services/api.service";
import { array } from "zod";


// Initialize TreeConfig with an empty object
const TreeConfig = ref<ITreeConfig>({
  roots: [],
  checkboxes: true,
  checkMode: "auto",
  dragAndDrop: false,
  editable: false,
});

// variables
const nodes = ref<INodes>({});
const route = useRoute();
const isLoadingCategories = ref<boolean>(true);
const categoriesLevel1 = ref(Array<ICategory>());
const categoriesAndChildren = ref(Array<ICategory>());
const selectedCategoriesUuids = ref<string[]>([]);
const selectedCategoriesFromProduct = ref<string[]>([]);
const loadCheckedItem = ref(1);

// Interfaces
interface ITreeConfig {
  roots: string[];
  checkboxes: boolean;
  checkMode: string;
  dragAndDrop: boolean;
  editable: boolean;
}
interface ITreeNodeState {
  opened: boolean;
  disabled: boolean;
  checked: boolean;
}

interface INodes {
  [id: string]: INode;
}

interface INode {
  categoryUuid: string
  text: string;
  children?: string[];
  state?: ITreeNodeState | null;
}

interface ICategory {
  id: string;
  uuid: string;
  text: string;
  state?: ITreeNodeState | null;
  children?: [] | ICategory[];
}

onMounted(async () => {
  await fetchCategoriesData();
});

// functions
async function fetchCategoriesData() {
  await ApiService.get("categories", {
    params: { lvl: 1, "order[lft]": "ASC" },
  })
    .then(async ({ data }) => {

      if (data.data.length > 0) {
        data.data.forEach((category, index: number) => {
          let id = index.toString();
          let newCategory: ICategory = {
            id: id,
            uuid: category.uuid,
            text: category.name,
            children: formatChildrenToCategory(category.children, id),
          };
          categoriesLevel1.value.push(newCategory);
          categoriesAndChildren.value.push(newCategory);
        });
        await hydrateCategoriesTreeview();

      }
    })
    .catch(() => {
      //swal.error("Une erreur a été détectée, veuillez re essayer 1.");
      return false;
    });
  isLoadingCategories.value = false;
}

function formatChildrenToCategory(data, parentKey: string) {
  let childrenData = Array<ICategory>();
  data.forEach((item, index: number) => {
    let key = parentKey + "-" + index;
    let newCategory: ICategory = {
      id: key,
      uuid: item.uuid,
      text: item.name,
      children:
        item.children && item.children.length
          ? formatChildrenToCategory(item.children, key)
          : [],
    };
    childrenData.push(newCategory);
    categoriesAndChildren.value.push(newCategory);
  });

  return childrenData;
}

async function hydrateCategoriesTreeview() {
  const productUuid = route.params.uuid;
  const response = await ApiService.get("/master-product/" + productUuid);
  let selectedCategoriesFromProductTemp = [];
    for (const item of response.data.data.categories) {
    selectedCategoriesFromProductTemp.push(item.uuid);
  }
  selectedCategoriesUuids.value = selectedCategoriesFromProductTemp;

  categoriesLevel1.value.forEach((category: ICategory) => {
    TreeConfig.value.roots.push(category.id);
  });
  if(loadCheckedItem.value ==1) {
    categoriesAndChildren.value.forEach((category: ICategory) => {
        let isChecked = false;
        if (selectedCategoriesUuids.value.indexOf(category.uuid) > -1) {
          isChecked = true;
        }
        const categoryNode: INode = {
          text: category.text,
          categoryUuid: category.uuid,
          state: {
            opened: false,
            disabled: false,
            checked: isChecked
          },
          children: category.children && category.children.length > 0
            ? getCategoryChildrenIds(category.children)
            : []
        };
        nodes.value[category.id] = categoryNode;
      }
    );
    loadCheckedItem.value = 0;
  } else {
    // Define Categories and Children
    categoriesAndChildren.value.forEach((category: ICategory) => {
      const categoryNode: INode = {
        text: category.text,
        categoryUuid: category.uuid,
        state: {
          opened: false,
          disabled: false,
          checked: false
        },
        children: category.children && category.children.length > 0
          ? getCategoryChildrenIds(category.children)
          : []
      };
      nodes.value[category.id] = categoryNode;
    });
  }
}

function getCategoryChildrenIds(categories: ICategory[]): string[]|[] {
  let categoryChildrenIds: string[] = [];
  categories.forEach((item: ICategory) => {
    categoryChildrenIds.push(item.id);
  });

  return categoryChildrenIds;
}

function onCategoryNodeChecked(event) {
  let categoryUuid = event.categoryUuid;
  if (!selectedCategoriesUuids.value.includes(categoryUuid)) {
    selectedCategoriesUuids.value.push(categoryUuid);
  }
}

function onCategoryNodeUnchecked(event) {
  let categoryUuid = event.categoryUuid;
  selectedCategoriesUuids.value = selectedCategoriesUuids.value.filter(uuid => uuid !== categoryUuid);
}
</script>
<style scoped lang="scss">
@import "vue3-treeview/dist/style.css";
</style>
